import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { Patient, Prescription } from '@/types';
import { formatPatientId } from '@/lib/utils/patientId';
import { buildPrescriptionPrintHtml } from '@/lib/utils/prescriptionPrintHtml';
import { openWhatsAppChat } from '@/lib/utils/whatsapp';

export type PrescriptionPdfShareResult = {
  success: boolean;
  message: string;
};

export async function generatePrescriptionPdfBlobFromHtml(
  fullDocumentHtml: string
): Promise<Blob> {
  const iframe = document.createElement('iframe');
  iframe.setAttribute(
    'style',
    'position:fixed;left:-10000px;top:0;width:794px;min-height:1123px;border:0;visibility:hidden'
  );
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) {
    document.body.removeChild(iframe);
    throw new Error('Could not create PDF preview frame');
  }

  doc.open();
  doc.write(fullDocumentHtml);
  doc.close();
  await new Promise((resolve) => setTimeout(resolve, 900));

  const pageEl = doc.querySelector('.prescription-page') ?? doc.body;
  if (!pageEl) {
    document.body.removeChild(iframe);
    throw new Error('PDF preview has no content');
  }

  const A4_HEIGHT_PX = 1123;
  const rawHeight = Math.ceil(Math.max(pageEl.scrollHeight, pageEl.getBoundingClientRect().height));
  const captureHeight = rawHeight <= A4_HEIGHT_PX + 8 ? A4_HEIGHT_PX : rawHeight;
  iframe.style.height = `${captureHeight}px`;
  if (pageEl instanceof HTMLElement) {
    pageEl.style.minHeight = `${captureHeight}px`;
    pageEl.style.height = `${captureHeight}px`;
  }

  const canvas = await html2canvas(pageEl as HTMLElement, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: pageEl.scrollWidth,
    height: captureHeight,
    windowWidth: pageEl.scrollWidth,
    windowHeight: captureHeight,
  });

  document.body.removeChild(iframe);

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const pageToleranceMm = 2;

  if (imgHeight <= pageHeight + pageToleranceMm) {
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
  } else {
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > pageToleranceMm) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
  }

  return pdf.output('blob');
}

function downloadPdfBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function sharePrescriptionPdfDownloadAndWhatsApp(params: {
  fullDocumentHtml: string;
  filename: string;
  patientName: string;
  hospitalName: string;
  prescriptionDate: string;
  phoneNumber?: string | null;
}): Promise<PrescriptionPdfShareResult> {
  const blob = await generatePrescriptionPdfBlobFromHtml(params.fullDocumentHtml);
  downloadPdfBlob(blob, params.filename);

  const message = `Hello ${params.patientName},

Your prescription from ${params.hospitalName} (${params.prescriptionDate}) is ready. Please download and save it for your records.

If you need any help, please contact our clinic.

Thank you.`;

  if (params.phoneNumber) {
    const opened = openWhatsAppChat(params.phoneNumber, message);
    if (!opened) {
      return {
        success: true,
        message: `PDF downloaded as ${params.filename}. Could not open WhatsApp - check the patient's phone number.`,
      };
    }
  }

  return {
    success: true,
    message: params.phoneNumber
      ? 'PDF downloaded. WhatsApp opened - attach the prescription PDF and send.'
      : `PDF downloaded as ${params.filename}. Add a patient contact number to open WhatsApp automatically.`,
  };
}

export function prescriptionPdfFilename(
  patientIdFormatted: string,
  prescriptionCreatedAt: string
): string {
  const safeId = patientIdFormatted.replace(/[^a-zA-Z0-9-]/g, '_');
  const datePart = format(new Date(prescriptionCreatedAt), 'yyyy-MM-dd');
  return `Prescription_${safeId}_${datePart}.pdf`;
}

export async function shareIssuedPrescriptionOnWhatsApp(params: {
  prescription: Prescription;
  patient: Patient;
  profile: {
    name: string;
    address?: string;
    phone1?: string;
    phone2?: string;
    phone3?: string;
    email?: string;
    website?: string;
    usePrescriptionHeader?: boolean;
  };
  doctors: Array<{ id: string; name: string; registrationNumber?: string }>;
}): Promise<PrescriptionPdfShareResult> {
  const { prescription, patient, profile, doctors } = params;
  const getDoctorName = (doctorId: string) =>
    doctors.find((d) => d.id === doctorId)?.name ?? 'Unknown Doctor';
  const getDoctorRegistration = (doctorId: string) =>
    doctors.find((d) => d.id === doctorId)?.registrationNumber ?? '';

  const prescriptionForPrint: Prescription = {
    ...prescription,
    patientId: prescription.patientId || patient.id,
    medications: prescription.medications?.map((med) => ({
      ...med,
      frequency: med.frequency || '',
      frequencyPattern: med.frequencyPattern || '',
      frequencyDescription: med.frequencyDescription || '',
    })) ?? [],
  };

  const fullDocumentHtml = buildPrescriptionPrintHtml({
    profile,
    patientName: patient.name,
    patientAge: patient.age,
    patientGender: patient.gender,
    prescription: prescriptionForPrint,
    getDoctorName,
    getDoctorRegistration,
  });

  const patientIdFormatted = formatPatientId(patient.id);
  const filename = prescriptionPdfFilename(patientIdFormatted, prescription.createdAt);
  return sharePrescriptionPdfDownloadAndWhatsApp({
    fullDocumentHtml,
    filename,
    patientName: patient.name,
    hospitalName: profile.name,
    prescriptionDate: format(new Date(prescription.createdAt), 'dd/MM/yyyy'),
    phoneNumber: patient.contactNumber,
  });
}
